// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One-way gate, with swing inertia (M1-15, PH-A25).
//
// The blocking half needs no new machinery at all: a gate is a **one-sided segment** whose
// normal faces the side it refuses, and the existing collider already passes a ball
// approaching from behind. What has to be added is the swing — because a gate that lets the
// ball through with no cost, no delay and no movement reads as a hole in the wall rather
// than as a mechanism.
//
// So the arm carries real rotational inertia. A ball crossing in the permitted direction
// lifts it, pays for the lift out of its own momentum, and the arm falls shut behind. While
// the arm is lifted the gate genuinely stops blocking, which means a ball can occasionally
// slip back through the wrong way behind another ball — an authentic and slightly infuriating
// pinball event that nobody has to write a special case for.

import {CapsuleChain} from '../Capsule'
import type { Ball } from '../Ball'
import type { Collider } from '../colliders'
import { makeSegment } from '../colliders'
import type { MaterialName } from '../materials'
import type { World } from '../World'
import { Device, type DeviceState } from './Device'
import { EV_GATE_PASSED } from '../events'
import { MAX_BALLS } from '../constants'
import { sin } from '../../math/deterministic'

export interface GateConfig {
  readonly mountContacts?:boolean
  readonly id: string
  /** Hinge end of the arm. */
  readonly a: readonly [number, number]
  /** Free end of the arm, in its closed position. */
  readonly b: readonly [number, number]
  /**
   * Direction a ball is permitted to travel through the gate, as a unit vector. It must be
   * perpendicular to `b − a`; the blocking face normal is its opposite.
   */
  readonly passDirection: readonly [number, number]
  /** Arm mass, kg. A wire gate is 2–4 g; that lightness is why a ball barely slows. */
  readonly armMass?: number
  /** Angle beyond which the arm is clear of the ball and stops blocking, radians. */
  readonly openAngle?: number
  /** Fully lifted angle, radians. */
  readonly maxAngle?: number
  /** Torque pulling the arm shut at full lift, N·m. */
  readonly closingTorque?: number
  /** Viscous damping, per second. */
  readonly damping?: number
  readonly material?: MaterialName
}

export class Gate extends Device {
  readonly kind = 'gate'
  override readonly wantsContact=true
  private readonly mounts:CapsuleChain
  override resolveContact(world:World,ball:Ball):boolean{return this.mounts.resolve(world,ball)}
  override readonly wantsObserve = true

  readonly ax: number
  readonly ay: number
  readonly bx: number
  readonly by: number
  /** Unit normal pointing the way a ball may travel. */
  readonly passX: number
  readonly passY: number

  private readonly armMass: number
  private readonly armLength: number
  private readonly inertia: number
  private readonly leverArm: number
  private readonly effectiveMass: number
  private readonly openAngle: number
  private readonly maxAngle: number
  private readonly closingTorque: number
  private readonly damping: number
  private readonly material: MaterialName

  /** Lift angle, radians. 0 is shut. */
  angle = 0
  /**
   * The lift angle at the end of the previous step. Written by the simulation, read by
   * renderers, so a gate flicked open by a passing ball interpolates instead of teleporting
   * between two frames' worth of swing (PH-022). Changes nothing about the dynamics.
   */
  prevAngle = 0
  /** Angular velocity, rad/s. */
  omega = 0

  protected override ownState(): DeviceState {
    // `side` is which side of the plane each ball was on last step, and it is the field a
    // hand-written snapshot forgets. Without it a restored gate treats the next observation as a
    // first sighting of every ball and lets one through in the direction it is there to refuse.
    return {
      angle: this.angle,
      prevAngle: this.prevAngle,
      omega: this.omega,
      side: [...this.side],
    }
  }

  protected override restoreOwn(state: DeviceState): void {
    this.angle = Number(state['angle'])
    this.prevAngle = Number(state['prevAngle'])
    this.omega = Number(state['omega'])
    const side = state['side']
    if (Array.isArray(side)) this.side.set(side as number[])
  }

  /** Signed side of the gate plane each ball was on last step. */
  private readonly side = new Int8Array(MAX_BALLS)

  constructor(config: GateConfig) {
    super(config.id)
    this.ax = config.a[0]
    this.ay = config.a[1]
    this.bx = config.b[0]
    this.by = config.b[1]
    this.setOrigin((this.ax + this.bx) / 2, (this.ay + this.by) / 2)

    const ex = this.bx - this.ax
    const ey = this.by - this.ay
    this.armLength = Math.sqrt(ex * ex + ey * ey)
    if (this.armLength === 0) throw new Error(`Gate ${config.id}: degenerate arm`)
    const nx=-ey/this.armLength,ny=ex/this.armLength
    this.mounts=new CapsuleChain(config.mountContacts&&this.armLength>=.020&&this.armLength<=.150?[{a:[this.ax-nx*.007,this.ay-ny*.007,.030],b:[this.ax-nx*.002,this.ay-ny*.002,.030],radius:.004}]:[])

    const p = config.passDirection
    const pl = Math.sqrt(p[0] * p[0] + p[1] * p[1])
    if (pl === 0) throw new Error(`Gate ${config.id}: zero-length passDirection`)
    this.passX = p[0] / pl
    this.passY = p[1] / pl

    const along = (ex * this.passX + ey * this.passY) / this.armLength
    if (Math.abs(along) > 1e-6) {
      throw new Error(
        `Gate ${config.id}: passDirection is not perpendicular to the arm (dot = ${along.toExponential(2)})`,
      )
    }

    this.armMass = config.armMass ?? 0.003
    this.openAngle = config.openAngle ?? 0.35
    this.maxAngle = config.maxAngle ?? 1.4
    this.closingTorque = config.closingTorque ?? 0.0012
    this.damping = config.damping ?? 6
    this.material = config.material ?? 'wire'

    // A uniform arm hinged at one end, struck at its midpoint. The effective mass a ball
    // feels there works out at exactly (4/3)·armMass, independent of the length — which is
    // why a light gate stays light however long it is.
    this.inertia = (this.armMass * this.armLength * this.armLength) / 3
    this.leverArm = this.armLength * 0.5
    this.effectiveMass = this.inertia / (this.leverArm * this.leverArm)
  }

  override colliders(): readonly Collider[] {
    // The face normal points the *same* way as the permitted direction, which reads
    // backwards until you work out which ball is where. A one-sided face only stops a ball
    // standing on its normal side. A ball travelling in the permitted direction approaches
    // from the *opposite* side, so it must be the opposite side that is ignored — which
    // means the normal has to point along `passDirection`, not against it. The ball the
    // gate turns away is the one already past it and coming back.
    return [
      makeSegment(this.ax, this.ay, this.bx, this.by, this.material, {
        oneSided: true,
        normal: [this.passX, this.passY],
        device: this.index,
      }),
    ]
  }

  override step(_world: World, dt: number): void {
    this.prevAngle = this.angle
    // Pendulum-style restoring torque: strongest at full lift, vanishing as it shuts, so
    // the arm settles rather than slamming.
    const torque = -this.closingTorque * sin(this.angle)
    this.omega += (torque / this.inertia) * dt
    this.omega -= this.omega * this.damping * dt
    this.angle += this.omega * dt

    if (this.angle <= 0) {
      this.angle = 0
      // A wire gate landing on its bracket does not bounce meaningfully; it clacks and stops.
      if (this.omega < 0) this.omega = 0
    } else if (this.angle >= this.maxAngle) {
      this.angle = this.maxAngle
      if (this.omega > 0) this.omega = -this.omega * 0.2
    }

    this.collidersActive = this.angle < this.openAngle
  }

  override observe(world: World, ball: Ball): void {
    const rel = (ball.x - this.ax) * this.passX + (ball.y - this.ay) * this.passY
    const now: number = rel >= 0 ? 1 : -1
    const was = this.side[ball.id]!
    this.side[ball.id] = now

    // First sighting of this ball: record which side it is on and wait.
    if (was === 0 || was === now) return
    // Only a crossing in the permitted direction is a pass; the other way was blocked.
    if (now !== 1) return
    if (!this.crossedWithinArm(ball)) return

    // Momentum exchange along the pass direction. The arm is perhaps a thirtieth of the
    // ball's mass, so the toll is a few per cent of speed — noticeable in a long orbit,
    // never enough to kill a shot.
    const vn = ball.vx * this.passX + ball.vy * this.passY
    const surface = this.omega * this.leverArm
    const approach = vn - surface
    if (approach <= 0) return

    const j = -approach / (ball.invMass + 1 / this.effectiveMass)
    ball.vx += j * this.passX * ball.invMass
    ball.vy += j * this.passY * ball.invMass
    this.omega -= (j * this.leverArm) / this.inertia

    world.events.emit(EV_GATE_PASSED, this.index, ball.id, world.stepIndex, vn)
  }

  /** Was the crossing inside the arm's span, rather than past its free end? */
  private crossedWithinArm(ball: Ball): boolean {
    const ex = (this.bx - this.ax) / this.armLength
    const ey = (this.by - this.ay) / this.armLength
    const u = (ball.x - this.ax) * ex + (ball.y - this.ay) * ey
    return u >= -ball.radius && u <= this.armLength + ball.radius
  }

  override reset(): void {
    this.angle = 0
    this.prevAngle = 0
    this.omega = 0
    this.collidersActive = true
    this.side.fill(0)
  }
}
